import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import { Button } from "@repo/ui/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@repo/ui/ui/dropdown-menu";
import { toast } from "sonner";

import { getRouter } from "~/router";
import { saveSqlAsDataset } from "~/stores/datasets/datasets.actions";
import { datasources$ } from "~/stores/datasources/datasources.crud";

/**
 * "More options" menu for a chat SQL block, rendered to the right of the copy
 * button. Currently exposes "Save as dataset", which creates a private dataset
 * from the fence-declared datasource. Renders nothing unless that datasource
 * still exists locally (strict, agent-declared only — also hides on stale ids
 * in old threads). Saving stays in chat; a toast offers a "View dataset" jump.
 */
export const SqlBlockActionsMenu = ({
  sql,
  datasourceId,
}: {
  sql: string;
  datasourceId: string;
}) => {
  const datasourceExists = useValue(
    () => !!datasources$[datasourceId]?.id.get(),
  );

  if (!datasourceExists) return null;

  const handleSave = () => {
    const datasetId = saveSqlAsDataset({ sql, datasourceId });
    toast.success("Saved as dataset", {
      action: {
        label: "View dataset",
        onClick: () => getRouter().navigate({ to: `/datasets/${datasetId}` }),
      },
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button size="icon-sm" variant="ghost">
            <Icon name="ellipsis-vertical" className="size-4" />
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-auto min-w-32">
        <DropdownMenuItem onClick={handleSave}>
          <Icon name="dataset" className="size-4" />
          Save as dataset
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
